import { auth } from '../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

// ─── Types ───────────────────────────────────────────────────────────

export type EventCategory = 'import' | 'review' | 'fetch' | 'pipeline' | 'notification' | 'snapshot';
export type EventStatus = 'success' | 'warning' | 'error' | 'info' | 'pending';

export interface TimelineEvent {
  id: string;
  category: EventCategory;
  timestamp: string;
  title: string;
  subtitle: string;
  status: EventStatus;
  actor: string;
  detail: Record<string, any>;
}

export interface TimelineResponse {
  success: boolean;
  events: TimelineEvent[];
  hasMore: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

// ─── API ─────────────────────────────────────────────────────────────

export async function getActivityTimeline(
  accountId: number,
  options?: {
    categories?: EventCategory[];
    limit?: number;
    before?: string;
  },
): Promise<TimelineResponse> {
  const headers = await getAuthHeaders();

  const params = new URLSearchParams();
  params.set('accountId', String(accountId));
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.before) params.set('before', options.before);
  if (options?.categories?.length) params.set('categories', options.categories.join(','));

  const res = await fetch(`${API_URL}/api/v2/activity/timeline?${params}`, { headers });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Activity API error ${res.status}`);
  }

  return res.json();
}
